import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { MENU_SECTION_LABEL } from '@/shared/ui-class';
import { allNavItems, isExecutiveNavId } from '@/versions/v1/shared/oem-nav';
import { currentDemoMode } from '@/versions/v1/shared/DemoModeContext';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import {
  useRowsDrawer,
  type SliceKey,
  type SliceRow,
} from '@/versions/v1/modules/service/source-slice';

// =========================================================================
// OemCommandPalette — the demo's `#mask` ⌘K surface. Fork-owned (zone C).
//
// Replaces the foundation's ThreadSpotlight in app/layout.tsx and inherits its
// contract: it opens on ⌘K / Ctrl-K and on the `app:open-palette` window
// event, so the review rail's search button and OemTopBar's ⌘K chip both keep
// working without knowing this component exists.
//
// Sources are pluggable. Navigation is registered here; machines
// (`fleet/palette.ts`), lake documents (`integration/documents-palette.ts`) and
// the eighteen other record kinds of the demo's `INDEX`
// (`chrome-oem/records-palette.ts`) register themselves by calling
// `registerPaletteSource()` once at module scope — no edit to this file.
// =========================================================================

/** A source-drawer slice opened on select — the demo's `audit(key, filter)`. */
export type PaletteSlice = {
  key: SliceKey;
  rows: readonly SliceRow[];
  /** Rows in the whole file, for the drawer's "1 of N" provenance. */
  total?: number;
  /** How the rows were chosen, e.g. `wo_id=WO-26-11842`. */
  filter?: string;
};

export type PaletteResult = {
  /** Unique within a source. */
  id: string;
  label: string;
  /** Second line — machine class, work-order status, section name. */
  hint?: string;
  icon?: LucideIcon;
  /** Record kind, printed as a chip before the label — `Work order`, `Email`. */
  kind?: string;
  /** Line under the label — sender, subject, technician and site. */
  detail?: string;
  /**
   * Route to navigate to on select. `to`, `slice` and `toast` say what
   * selecting the row does: set at least one. They run in this order, so a
   * work order can land on its machine and open its own row on top.
   */
  to?: string;
  /** Source-drawer slice to open. */
  slice?: PaletteSlice;
  /** Toast to raise — the demo's person rows show an email address. */
  toast?: string;
};

export type PaletteSource = {
  /** Stable key; re-registering the same key replaces the source. */
  id: string;
  /** Group heading in the result list. */
  label: string;
  /** Lower sorts first. Navigation is 100; entity sources should sit below. */
  order: number;
  /**
   * Return matches for `query`, already ranked. Keep it synchronous and cheap
   * — it runs on every keystroke. `query` is lower-cased and trimmed; it is
   * empty when the palette first opens, which should return a short default
   * list rather than everything.
   */
  search: (query: string) => PaletteResult[];
  /**
   * True while the source is still loading what it searches. The palette says
   * so instead of claiming nothing matches.
   */
  pending?: () => boolean;
};

const sources = new Map<string, PaletteSource>();
const listeners = new Set<() => void>();

/**
 * Register (or replace) a palette source. Call once at module scope from the
 * module that owns the data.
 *
 * @returns an unregister function, for hot-module reload and tests.
 *
 * @example
 * registerPaletteSource({
 *   id: 'machines',
 *   label: 'Machines',
 *   order: 10,
 *   search: (q) => FLEET.filter(f => f.machine_id.toLowerCase().includes(q))
 *     .slice(0, 8)
 *     .map(f => ({ id: f.machine_id, label: f.machine_id, hint: f.model,
 *                  to: `/machines/${f.machine_id}` })),
 * });
 */
export function registerPaletteSource(source: PaletteSource): () => void {
  sources.set(source.id, source);
  listeners.forEach((fn) => fn());
  return () => {
    sources.delete(source.id);
    listeners.forEach((fn) => fn());
  };
}

/** Current sources, lowest `order` first. */
export function paletteSources(): PaletteSource[] {
  return [...sources.values()].sort((a, b) => a.order - b.order);
}

// ---- built-in source: navigation ----------------------------------------

registerPaletteSource({
  id: 'navigation',
  label: 'Navigate',
  order: 100,
  search: (query) => {
    // fork(P7a/A1): the palette searches **every** row in both demo modes.
    // Executive mode shortens the rail, not the app — being able to type
    // `customers` and land on a surface the rail no longer lists is the whole
    // reason the cut is an allowlist rather than a tier flip. Mode only
    // decides the order: rows the executive rail carries sort first, so the
    // six-click story stays one keystroke away.
    const executive = currentDemoMode() === 'executive';
    const rows = allNavItems().map(({ section, item }) => ({
      id: item.id,
      label: item.label,
      hint: section.label,
      icon: item.icon,
      to: item.to,
      rank: executive && isExecutiveNavId(item.id) ? 0 : 1,
    }));
    // Stable within a rank — `sort` is stable in every engine we ship to, so
    // nav order survives inside each group.
    rows.sort((a, b) => a.rank - b.rank);
    if (!query) return rows.slice(0, 6);
    return rows
      .filter(
        (r) =>
          r.label.toLowerCase().includes(query) ||
          r.hint.toLowerCase().includes(query),
      )
      .slice(0, 10);
  },
});

// -------------------------------------------------------------------------

/** The demo's empty-state suggestions (line 2542), one click to try each. */
const SEARCH_HINTS = [
  'coupling',
  'Kalbarri breakdown',
  'brake',
  'Night B',
  'MER-RD-FE-KIT',
  'seismic',
] as const;

const BACKDROP =
  'fixed inset-0 z-[250] flex items-start justify-center bg-[rgba(26,19,15,0.42)] px-4 pb-6 pt-[16vh] backdrop-blur-md';
const CARD =
  'relative flex w-full max-w-[640px] flex-col overflow-hidden rounded-2xl border border-[var(--glass-border-strong)] bg-[var(--glass-surface-strong)] shadow-[0_24px_64px_-12px_rgba(20,24,40,0.28)] backdrop-blur-2xl';

export function OemCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [sourceEpoch, setSourceEpoch] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const openRows = useRowsDrawer();

  // Re-render when a lane registers a new source after first paint.
  useEffect(() => {
    const bump = () => setSourceEpoch((n) => n + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('app:open-palette', onOpen);
    return () => window.removeEventListener('app:open-palette', onOpen);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const editable =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (editable && el !== inputRef.current) return;
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && open) {
        if (editable && el !== inputRef.current) return;
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const { groups, pending } = useMemo(() => {
    // Closed, search nothing: a source's first `search()` is what starts its
    // lazy load, and that should wait for someone to open the palette.
    if (!open) return { groups: [], pending: false };
    const q = query.trim().toLowerCase();
    const sources = paletteSources();
    return {
      groups: sources
        .map((source) => ({ source, results: source.search(q) }))
        .filter((g) => g.results.length > 0),
      pending: sources.some((source) => source.pending?.() ?? false),
    };
    // sourceEpoch forces recomputation when a lane registers late.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, sourceEpoch]);

  /** Flat list, so ↑/↓ crosses group boundaries. */
  const flat = useMemo(
    () => groups.flatMap((g) => g.results.map((r) => ({ ...r, group: g.source.id }))),
    [groups],
  );

  const go = useCallback(
    (result: PaletteResult) => {
      setOpen(false);
      if (result.to) navigate(result.to);
      if (result.slice) {
        const { key, rows, total, filter } = result.slice;
        openRows(key, rows, { total, filter });
      }
      if (result.toast) showToast(result.toast);
    },
    [navigate, openRows, showToast],
  );

  const tryHint = (hint: string) => {
    setQuery(hint);
    setActive(0);
    inputRef.current?.focus();
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[active];
      if (target) go(target);
    }
  };

  if (!open) return null;

  let cursor = -1;

  const modal = (
    <Commentable name="OemCommandPalette">
      <div
        className={BACKDROP}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div className={CARD} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 px-4 focus-within:ring-2 focus-within:ring-[color-mix(in_srgb,var(--color-accent-500)_20%,transparent)]">
            <Search className="h-4 w-4 flex-shrink-0 text-zinc-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls="oem-palette-results"
              aria-autocomplete="list"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onInputKey}
              placeholder="Search machines, work orders, parts, people, email, transcripts…"
              aria-label="Search"
              className="w-full bg-transparent py-4 text-base font-medium text-[var(--foreground)] outline-none placeholder:text-zinc-400 focus:outline-none"
            />
          </div>

          <div
            id="oem-palette-results"
            role="listbox"
            className="max-h-[52vh] overflow-y-auto"
          >
            {query.trim() === '' ? (
              <p className="flex flex-wrap items-center gap-1.5 border-t border-[var(--glass-border)] px-5 py-3 text-xs text-muted">
                <span>Try</span>
                {SEARCH_HINTS.map((hint) => (
                  <button
                    key={hint}
                    type="button"
                    onClick={() => tryHint(hint)}
                    className="rounded-md border border-[var(--glass-border)] px-1.5 py-0.5 font-medium text-zinc-600 transition-colors hover:bg-[var(--glass-surface-strong)] hover:text-accent-700 dark:text-zinc-300 dark:hover:text-accent-300"
                  >
                    {hint}
                  </button>
                ))}
              </p>
            ) : null}
            {flat.length === 0 && query.trim() !== '' ? (
              <p className="border-t border-[var(--glass-border)] px-5 py-6 text-sm text-muted">
                {pending ? 'Indexing records…' : `Nothing matches “${query}”.`}
              </p>
            ) : (
              groups.map((group) => (
                <div
                  key={group.source.id}
                  className="flex flex-col gap-px border-t border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-2 py-2"
                >
                  <h2 className={cn(MENU_SECTION_LABEL, 'px-1')}>
                    {group.source.label}
                  </h2>
                  {group.results.map((result) => {
                    cursor += 1;
                    const index = cursor;
                    const Icon = result.icon;
                    const selected = index === active;
                    return (
                      <button
                        key={`${group.source.id}:${result.id}`}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => go(result)}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm outline-none transition-colors',
                          selected
                            ? 'bg-[var(--glass-surface-strong)] text-accent-700 dark:text-accent-300'
                            : 'text-zinc-700 dark:text-zinc-300',
                        )}
                      >
                        {Icon ? (
                          <Icon
                            className="h-4 w-4 flex-shrink-0 text-zinc-400"
                            strokeWidth={1.6}
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                        )}
                        {result.kind ? (
                          <span className="w-[92px] flex-shrink-0 truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-zinc-400">
                            {result.kind}
                          </span>
                        ) : null}
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate font-medium">{result.label}</span>
                          {result.detail ? (
                            <span className="truncate text-xs font-normal text-muted">
                              {result.detail}
                            </span>
                          ) : null}
                        </span>
                        {result.hint ? (
                          <span className="flex-shrink-0 text-xs text-muted">
                            {result.hint}
                          </span>
                        ) : null}
                        {selected ? (
                          <CornerDownLeft
                            className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400"
                            aria-hidden="true"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Commentable>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
